// One invoice-generator core for the Traffic / Salik / Parking tax invoices (audit DUP-21, owner 26 Sep 2026).
//
// The three generators were ~1,250-line copies of each other (row check, preview, single page, consolidated
// page, reference numbering) that had drifted: Parking printed the invoice date as yyyy-mm-dd; Salik read text
// dates day-first, the others did not. Here each kind is a small config (its columns, labels, description
// lines, VAT rule and numbering) and everything else is shared.
//
// VAT (audit FIN-12): every invoice prints Net / VAT 5% / TOTAL under its table. Traffic and Salik totals are
// unchanged (the amount of the sheet / the form, VAT now shown); Parking adds its VAT:
//   Salik    the price has the 5 % VAT inside it (toll with VAT + AED 1 a trip + VAT): VAT = total / 21
//   Traffic  the amount is the billed fine (fine + 5 % fee + VAT on the fee): VAT = the VAT on the fee only
//   Parking  the amount is NET: VAT 5 % is added on top (owner, 26 Sep 2026) - total = amount x 1.05 (100 -> 105)
import { parseAmount, billedFineLine, vatInclusiveLine, netLine, invoiceTotals } from '../../../constants/finance';
import { formatInvoiceDate } from './invoiceDates';
import { isInvygoNumber } from '../../../services/contractRules';
import { drawInvoiceTable, ensureSpace, letterheadPage } from './pdfTable';

const text = (v) => (v === null || v === undefined ? '' : String(v));
const fix2 = (n) => (Number(n) || 0).toFixed(2);

// -- the kinds --------------------------------------------------------------------------------------------------

const salikDateText = (row) => {
    if (text(row['Month']).trim()) return `Salik Month: ${row['Month']}`;
    const start = formatInvoiceDate(row['Date']);
    const end = formatInvoiceDate(row['End Date']);
    if (!start) return '';
    return end ? `Salik Date: ${start} - ${end}` : `Salik Date: ${start}`;
};
const salikTrips = (row) => parseInt(row['Salik Trips'] || 0, 10) || 0;
const hasTrips = (row) => text(row['Salik Trips']).trim() !== '';
const parkingHours = (row) => `${text(row['Time'] || '0').replace(/hrs?/gi, '').trim()} hours`;

const TRAFFIC_FIELDS = ['TFINE No.', 'Plate_Number', 'Date', 'Time', 'Amount', 'Description', 'Dealer_Booking_Number', 'Booking_ID', 'Invoice_Date', 'Tax_Invoice_No', 'Customer'];

export const INVOICE_KINDS = {
    traffic: {
        id: 'traffic',
        title: 'Traffic Fines Invoices',
        subtitle: 'Generate Traffic Fines invoices via Upload or Manual Entry',
        filePrefix: 'Traffic',
        amountKey: 'Amount',
        amountLabel: 'Amount (AED)',
        invoiceNoKey: 'Tax_Invoice_No',
        accept: '.csv',
        uploadLabel: 'Click to Upload Excel/CSV File',
        customerOf: (row) => row['Customer'] || row['Customer_Name'] || '',
        identifierOf: (row) => row['Plate_Number'] || row['TFINE No.'] || row['Customer'] || row['Customer_Name'] || '',
        fileIdOf: (row) => row['TFINE No.'] || row['Plate_Number'],
        split: billedFineLine,
        vatLabel: 'VAT 5% (on the service fee):',
        greeting: ['Traffic fine details given below;'],
        consolidatedSubject: 'SUB: Micro Lease Cars - Consolidated Traffic Fines',
        widths: { no: 15, total: 35 },
        cellMoney: (n) => `${fix2(n)} AED`,
        extra: null,
        description: (row) => [
            row['Customer'] || row['Customer_Name'] || 'N/A',
            `Traffic Fine No: ${text(row['TFINE No.'])}`,
            `Date: ${formatInvoiceDate(row['Date'])}`,
            `Time: ${text(row['Time'])}`,
            `Booking ID: ${text(row['Booking_ID'] || row['Dealer_Booking_Number'])}`,
            `R/A: ${text(row['Dealer_Booking_Number'])}`,
            `Plate No. ${text(row['Plate_Number'])}`,
            `Description: ${text(row['Description'])}`,
        ],
        consolidatedHead: (rows, customers) => [customers.length <= 1 ? (customers[0] || '') : 'Customers: see each line', 'Fines:'],
        consolidatedLine: (row) => `${text(row['Plate_Number'])} - ${text(row['TFINE No.'])} (${text(row['Date'])} ${text(row['Time'])}) - ${text(row['Description'])}`,
        emptyRow: () => Object.fromEntries(TRAFFIC_FIELDS.map((k) => [k, ''])),
        fromContract: (res) => ({ 'Customer': res.customer || '', 'Plate_Number': res.plate_number || '', 'Dealer_Booking_Number': res.contract_no || '', 'Booking_ID': res.booking_number || '' }),
        fields: [
            { key: 'Customer', label: 'Customer Name', placeholder: 'Customer Name' },
            { key: 'Plate_Number', label: 'Plate No.', placeholder: 'Plate No' },
            { key: 'TFINE No.', label: 'Traffic Fine No.', placeholder: 'Fine No' },
            { key: 'Booking_ID', label: 'Booking ID', placeholder: 'Booking ID' },
            { key: 'Dealer_Booking_Number', label: 'R/A (Booking No)', placeholder: 'Dealer Bk No' },
            { key: 'Tax_Invoice_No', label: 'Tax Invoice No', placeholder: 'Optional' },
            { key: 'Date', label: 'Fine Date', placeholder: 'DD/MM/YYYY' },
            { key: 'Time', label: 'Fine Time', placeholder: 'HH:MM' },
            { key: 'Amount', label: 'Amount billed (AED)', placeholder: '0.00', type: 'number', wide: true, help: 'The billed amount: the fine + 5% service fee + VAT on the fee (500 fine -> 526.25).' },
            { key: 'Description', label: 'Description', placeholder: 'Violation Description', wide: true },
        ],
        template: { type: 'csv', name: 'Traffic-Fines-Template.csv', headers: TRAFFIC_FIELDS },
        csvMirror: true,
    },
    salik: {
        id: 'salik',
        title: 'Salik Invoices',
        subtitle: 'Generate Salik invoices via Upload or Manual Entry',
        filePrefix: 'Salik',
        amountKey: 'Total Price',
        invoiceNoKey: 'INVOICE',
        accept: '.xlsx,.xls,.csv',
        uploadLabel: 'Click to Upload Excel/CSV File',
        customerOf: (row) => row['Customer'] || '',
        identifierOf: (row) => row['Plate No.'] || row['Booking Number'] || row['Customer'] || '',
        fileIdOf: (row) => row['INVOICE'] || row['Booking Number'] || row['Plate No.'],
        split: vatInclusiveLine,
        vatLabel: 'VAT 5%:',
        greeting: ['Dear Sir,', 'We thank you for your business renting the below vehicle.'],
        greetingMany: ['Dear Sir,', 'We thank you for your business renting the below vehicles.'],
        consolidatedSubject: 'SUB: Micro Lease Cars - Consolidated Salik',
        widths: { no: 15, total: 30 },
        cellMoney: fix2,
        extra: {
            title: 'Salik Trips',
            width: 30,
            show: (rows) => rows.some(hasTrips),
            showMany: (rows) => rows.some((r) => salikTrips(r) > 0),
            cell: (row) => `${text(row['Salik Trips'])} Trips`,
            total: (rows) => `${rows.reduce((a, r) => a + Math.max(0, salikTrips(r)), 0)} Trips`,
        },
        description: (row) => [
            `Name: ${text(row['Customer'])}`,
            `Booking ID: ${text(row['Booking Number'])}`,
            `R/A: ${text(row['Contract No.'])}`,
            `Vehicle: ${text(row['Model'])} - ${text(row['Plate No.'])}`,
            salikDateText(row),
        ],
        consolidatedHead: (rows) => {
            const distinct = (key) => [...new Set(rows.map((r) => r[key] || '').filter(Boolean))];
            const oneOf = (list) => (list.length <= 1 ? (list[0] || '') : 'see each line');
            return [`Name: ${oneOf(distinct('Customer'))}`, `Booking ID: ${oneOf(distinct('Booking Number'))}`,
                `R/A: ${oneOf(distinct('Contract No.'))}`, 'Vehicles:'];
        },
        perLineOwner: (rows) => ['Customer', 'Booking Number', 'Contract No.'].some((k) => new Set(rows.map((r) => r[k] || '').filter(Boolean)).size > 1),
        ownerOf: (row) => [row['Customer'], row['Contract No.']].filter(Boolean).join(' / '),
        consolidatedLine: (row) => {
            let dateText = text(row['Month']);
            if (!dateText) dateText = row['End Date'] ? `${text(row['Date'])} - ${text(row['End Date'])}` : text(row['Date']);
            const trips = salikTrips(row);
            return `${text(row['Plate No.'])} - ${text(row['Model'])} (${dateText}${trips > 0 ? `, ${trips} Trips` : ''})`;
        },
        emptyRow: () => ({ 'Customer': '', 'Booking Number': '', 'Contract No.': '', 'Model': '', 'Plate No.': '', 'Date': '', 'End Date': '', 'Month': '', 'Salik Trips': '', 'Total Price': '' }),
        fromContract: (res) => ({ 'Customer': res.customer || '', 'Booking Number': res.booking_number || '', 'Contract No.': res.contract_no || '', 'Model': res.model || '', 'Plate No.': res.plate_number || '' }),
        fields: [
            { key: 'Customer', label: 'Customer Name', placeholder: 'Customer Name' },
            { key: 'Plate No.', label: 'Plate No.', placeholder: 'Plate No' },
            { key: 'Booking Number', label: 'Booking ID', placeholder: 'Booking ID' },
            { key: 'Contract No.', label: 'Contract No', placeholder: 'Contract No' },
            { key: 'Model', label: 'Model', placeholder: 'Vehicle Model', wide: true },
            { key: 'Month', label: 'Salik Month', placeholder: 'e.g. October 2023' },
            { key: 'Salik Trips', label: 'No. of Trips', placeholder: '0', type: 'number' },
            { key: 'Date', label: 'Start Date', placeholder: 'DD/MM/YYYY' },
            { key: 'End Date', label: 'End Date', placeholder: 'DD/MM/YYYY' },
            { key: 'Total Price', label: 'Total Price (AED, VAT inclusive)', placeholder: '0.00', type: 'number', wide: true },
        ],
        template: { type: 'xlsx', name: 'Salik-Invoice-Template.xlsx',
            headers: ['INVOICE', 'Customer', 'Booking Number', 'Contract No.', 'Model', 'Plate No.', 'Date', 'End Date', 'Month', 'Salik Trips', 'Total Price', 'Invoice_Date'] },
    },
    parking: {
        id: 'parking',
        title: 'Parking Invoices',
        subtitle: 'Generate Parking invoices via Upload or Manual Entry',
        filePrefix: 'Parking',
        amountKey: 'Amount',
        invoiceNoKey: 'Tax_Invoice_No',
        accept: '.xlsx,.xls,.csv',
        uploadLabel: 'Click to Upload Excel/CSV File',
        customerOf: (row) => row['Customer Name'] || row['Customer'] || '',
        identifierOf: (row) => row['Plate_Number'] || row['Dealer_Booking_Number'] || row['Customer Name'] || row['Customer'] || '',
        fileIdOf: (row) => row['Tax_Invoice_No'] || row['Dealer_Booking_Number'] || row['Plate_Number'],
        split: (amount) => netLine(amount),   // net amount, VAT 5 % added
        vatLabel: 'VAT 5%:',
        greeting: ['Dear Sir,', 'We thank you for your business renting the below vehicle.'],
        greetingMany: ['Dear Sir,', 'We thank you for your business renting the below vehicles.'],
        consolidatedSubject: 'SUB: Micro Lease Cars - Consolidated Parking',
        widths: { no: 15, total: 30 },
        consolidatedWidths: { no: 15, total: 35 },
        cellMoney: fix2,
        consolidatedCellMoney: (n) => `${fix2(n)} AED`,
        extra: { title: 'Duration', width: 30, show: () => true, cell: parkingHours, total: null },
        description: (row) => [
            `Name: ${text(row['Customer Name'] || row['Customer'])}`,
            `Booking ID: ${text(row['Dealer_Booking_Number'])}`,
            `Contract: ${text(row['Contract'])}`,
            `Vehicle: ${text(row['Model'])} - ${text(row['Plate_Number'])}`,
            `Date: ${formatInvoiceDate(row['Date'])}`,
            `Time In: ${text(row['Time_In'])}`,
            `Time Out: ${text(row['Time_Out'])}`,
        ],
        consolidatedHead: (rows, customers) => [
            `Name: ${customers.length <= 1 ? (customers[0] || '') : 'see each line'}`,
            `Booking ID: ${text((rows[0] || {})['Dealer_Booking_Number'])}`,
            `Contract: ${text((rows[0] || {})['Contract'])}`,
            'Parking Details:',
        ],
        consolidatedLine: (row) => `${text(row['Plate_Number'])} - ${text(row['Model'])} (${text(row['Date'])} ${text(row['Time_In'])}-${text(row['Time_Out'])} ${row['Time'] ? `(${row['Time']}h)` : ''})`,
        emptyRow: () => ({ 'Customer Name': '', 'Dealer_Booking_Number': '', 'Contract': '', 'Model': '', 'Plate_Number': '', 'Date': '', 'Time_In': '', 'Time_Out': '', 'Time': '', 'Amount': '' }),
        fromContract: (res) => ({ 'Customer Name': res.customer || '', 'Dealer_Booking_Number': res.booking_number || '', 'Contract': res.contract_no || '', 'Model': res.model || '', 'Plate_Number': res.plate_number || '' }),
        // the manual form types 'Customer Name'; the sheet may say 'Customer'
        normalizeManual: (r) => ({ ...r, 'Customer': r['Customer Name'] }),
        fields: [
            { key: 'Customer Name', label: 'Customer Name', placeholder: 'Customer Name' },
            { key: 'Plate_Number', label: 'Plate Number', placeholder: 'Plate No' },
            { key: 'Model', label: 'Model', placeholder: 'Car Model' },
            { key: 'Contract', label: 'Contract No.', placeholder: 'Contract No' },
            { key: 'Dealer_Booking_Number', label: 'Booking ID', placeholder: 'Booking ID' },
            { key: 'Date', label: 'Entry Date', placeholder: 'DD/MM/YYYY' },
            { key: 'Time_In', label: 'Time In', placeholder: 'HH:MM' },
            { key: 'Time_Out', label: 'Time Out', placeholder: 'HH:MM' },
            { key: 'Time', label: 'Duration', placeholder: 'Hours' },
            { key: 'Amount', label: 'Amount (AED, before VAT)', placeholder: '0.00', type: 'number', help: 'VAT 5% is added on top.' },
        ],
        template: { type: 'xlsx', name: 'Parking-Invoice-Template.xlsx',
            headers: ['Tax_Invoice_No', 'Customer Name', 'Dealer_Booking_Number', 'Contract', 'Model', 'Plate_Number', 'Date', 'Time_In', 'Time_Out', 'Time', 'Amount', 'Invoice_Date'] },
    },
};

// -- rows -------------------------------------------------------------------------------------------------------

/**
 * The "Booking ID" a Smart-search contract fills in (it goes on the tax invoice and into "Invygo Upload.csv"). An
 * Invygo contract's booking is INVYGO's own id - Invygo files its invoices by it (owner, 28 Sep 2026) - from
 * `invygoIds` (src/services/invygoIds.js: Map(CONTRACT NO -> { invygoId })), and '' when it is not known: never
 * Ejar's own booking number (B26000D04442492), which the contract record carries now that the Invygo sheet no longer
 * writes Invygo's id over it. Any other contract keeps its own booking number, as before.
 */
export function invoiceBookingOf(res, invygoIds) {
    const no = text(res && res.contract_no).trim().toUpperCase();
    const known = invygoIds && no ? invygoIds.get(no) : null;
    if (known && text(known.invygoId).trim()) return text(known.invygoId).trim();
    if (isInvygoNumber(no) || /invygo/i.test(text(res && (res.pickup_branch || res.branch)))) return '';
    return text(res && res.booking_number);
}

/**
 * Drops the rows whose amount cannot be read as a number (a malformed / empty cell), so a bad row never turns a
 * total or a PDF value into NaN. Returns the valid rows (amount as a number) and the skipped rows described.
 */
export function filterRowsWithValidAmount(kind, rows) {
    const validRows = [];
    const skipped = [];
    (rows || []).forEach((row, idx) => {
        const amt = parseAmount(row[kind.amountKey]);
        if (amt === null) {
            const identifier = kind.identifierOf(row);
            skipped.push(`row ${idx + 1}${identifier ? ` (${identifier})` : ''}`);
        } else {
            validRows.push({ ...row, [kind.amountKey]: amt });
        }
    });
    return { validRows, skipped };
}

/** The amount of a row (0 when unreadable). */
export const amountOf = (kind, row) => parseAmount(row[kind.amountKey]) ?? 0;

/** Net / VAT / Total of one invoice made of these rows (each line rounded, the totals their sums). */
export const invoiceAmounts = (kind, rows) => invoiceTotals(rows.map((r) => kind.split(amountOf(kind, r))));

/** The Net / VAT 5% / TOTAL rows printed under an invoice table. */
export function totalRows(kind, amounts, labelFrom = 1) {
    return [
        { label: 'Net Amount:', value: `AED ${fix2(amounts.net)}`, labelFrom },
        { label: kind.vatLabel, value: `AED ${fix2(amounts.vat)}`, labelFrom },
        { label: 'TOTAL:', value: `AED ${fix2(amounts.total)}`, labelFrom },
    ];
}

/** The invoice date on the page: the row's own Invoice_Date when the sheet has one, else the chosen date. */
export function invoiceDateOf(row, chosen) {
    const own = row && row['Invoice_Date'] ? formatInvoiceDate(row['Invoice_Date']) : '';
    return own || formatInvoiceDate(chosen);
}

/** The reference printed on a row's invoice: the number issued for it, else the sheet's own number. */
export const refOf = (kind, row) => text(row.RefNumber || row[kind.invoiceNoKey]);

/** Rows that need a number from the invoice-number service (manual rows always; sheet rows without one). */
export const rowsWithoutNumber = (kind, rows) => rows.filter((r) => !text(r[kind.invoiceNoKey]).trim());

/** The table (columns, one row, total rows) of the single invoice of `row`. */
export function singleTable(kind, row, contentWidth = 180) {
    const showExtra = kind.extra && kind.extra.show([row]);
    const { no, total } = kind.widths;
    const extraW = showExtra ? kind.extra.width : 0;
    const columns = [{ title: 'No.', width: no }, { title: 'Description', width: contentWidth - no - total - extraW },
        ...(showExtra ? [{ title: kind.extra.title, width: extraW }] : []), { title: 'Total Price', width: total }];
    const amounts = invoiceAmounts(kind, [row]);
    const cells = ['1', kind.description(row), ...(showExtra ? [kind.extra.cell(row)] : []), kind.cellMoney(amounts.total)];
    return { columns, rows: [cells], totals: totalRows(kind, amounts, showExtra ? 2 : 1), amounts };
}

/** The table of one consolidated invoice of all `rows`: every line in one cell, one total. */
export function consolidatedTable(kind, rows, contentWidth = 180) {
    const customers = [...new Set(rows.map(kind.customerOf).filter(Boolean))];
    const perLine = kind.perLineOwner ? kind.perLineOwner(rows) : customers.length > 1;
    const owner = (row) => (perLine ? `${kind.ownerOf ? kind.ownerOf(row) : kind.customerOf(row)}: ` : '');
    const lines = [...kind.consolidatedHead(rows, customers), ...rows.map((r) => `• ${owner(r)}${kind.consolidatedLine(r)}`)];
    const extraTotal = kind.extra && kind.extra.total && (kind.extra.showMany || kind.extra.show)(rows);
    const { no, total } = kind.consolidatedWidths || kind.widths;
    const extraW = extraTotal ? kind.extra.width : 0;
    const columns = [{ title: 'No.', width: no }, { title: 'Description', width: contentWidth - no - total - extraW },
        ...(extraTotal ? [{ title: kind.extra.title, width: extraW }] : []), { title: 'Total Price', width: total }];
    const amounts = invoiceAmounts(kind, rows);
    const money = kind.consolidatedCellMoney || kind.cellMoney;
    const cells = ['1', lines, ...(extraTotal ? [kind.extra.total(rows)] : []), money(amounts.total)];
    return { columns, rows: [cells], totals: totalRows(kind, amounts, extraTotal ? 2 : 1), amounts };
}

// -- PDF --------------------------------------------------------------------------------------------------------

const PAGE_W = 210;
const MARGIN = 15;

function drawHeader(doc, { imgData, date, ref, trn, companyName, companyLocation, subject, greeting }) {
    doc.addImage(imgData, 'JPEG', 0, 0, PAGE_W, 297);
    doc.setTextColor(0, 0, 0);
    let y = 35;
    doc.setFontSize(26);
    doc.setFont('helvetica', 'bold');
    doc.text('Tax Invoice', PAGE_W / 2, y, { align: 'center' });
    y += 15;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Date: ${date}`, MARGIN, y);
    const refText = `Ref: ${ref}`;
    doc.text(refText, PAGE_W - MARGIN - doc.getTextWidth(refText), y);
    y += 7;
    doc.text(`TRN#: ${trn}`, MARGIN, y);
    y += 15;
    doc.text(text(companyName), MARGIN, y);
    y += 5;
    doc.text(text(companyLocation), MARGIN, y);
    y += 5;
    doc.text('Dubai, U.A.E.', MARGIN, y);
    y += 15;
    doc.text(subject, MARGIN, y);
    y += 15;
    greeting.forEach((line, i) => {
        doc.text(line, MARGIN, y);
        y += i === greeting.length - 1 ? 15 : 7;
    });
    return y;
}

function drawFooter(doc, y, newPage, full) {
    y = ensureSpace(y + 20, full ? 60 : 30, newPage);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    if (full) {
        doc.text('General Conditions:', MARGIN, y);
        doc.line(MARGIN, y + 1, MARGIN + 35, y + 1);
        y += 10;
        doc.text('Terms of Payment : within 7 days', MARGIN, y);
        y += 15;
        doc.text('Thanking you and assuring you of our best co-operation and services at all times.', MARGIN, y);
        y += 15;
    } else {
        doc.text('Terms of Payment : within 7 days', MARGIN, y);
        y += 10;
    }
    doc.text('Best Regards,', MARGIN, y);
    y += 10;
    doc.setFont('helvetica', 'bold');
    doc.text('Saudian Alwefaq Rent A Car', MARGIN, y);
}

/** One row's invoice on the letterhead (the table continues on a new page when it does not fit). */
export function drawSingleInvoice(doc, kind, row, { imgData, date, trn, companyName, companyLocation }) {
    let y = drawHeader(doc, { imgData, date, ref: refOf(kind, row), trn, companyName, companyLocation,
        subject: 'SUB: Micro Lease Cars', greeting: kind.greeting });
    const table = singleTable(kind, row);
    const newPage = letterheadPage(doc, imgData);
    y = drawInvoiceTable(doc, { x: MARGIN, y: y - 5, columns: table.columns, rows: table.rows, total: table.totals, newPage });
    drawFooter(doc, y, newPage, true);
    return table.amounts;
}

/** One invoice for all `rows` (numbered `ref`). */
export function drawConsolidatedInvoice(doc, kind, rows, { imgData, date, ref, trn, companyName, companyLocation }) {
    let y = drawHeader(doc, { imgData, date, ref, trn, companyName, companyLocation,
        subject: kind.consolidatedSubject, greeting: kind.greetingMany || kind.greeting });
    const table = consolidatedTable(kind, rows);
    const newPage = letterheadPage(doc, imgData);
    y = drawInvoiceTable(doc, { x: MARGIN, y: y - 5, columns: table.columns, rows: table.rows, total: table.totals, newPage });
    drawFooter(doc, y, newPage, false);
    return table.amounts;
}
